import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext'; // Import Auth Logic
import LanguageSwitcher from './LanguageSwitcher';
import logo from '../../utils/static/logo.png';
import {
  MapPinIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  UserIcon,
  Cog6ToothIcon,
  HomeIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';

const Navbar = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { user, logout } = useAuth(); // Get user and logout function

  // Mobile profile menu state
  const [isMobileProfileOpen, setIsMobileProfileOpen] = useState(false);
  const profileMenuRef = useRef(null);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsMobileProfileOpen(false);
      }
    };

    if (isMobileProfileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileProfileOpen]);

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="bg-[#002338] backdrop-blur-md border-b border-gray-200 z-[3000] sticky top-0 h-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">

          {/* Brand */}
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700">
              <img src={logo} alt="Brand logo" className="h-10 w-auto rounded-md object-cover shadow-sm" />
            </Link>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-4">
            <div className="flex gap-1 bg-white border border-gray-100 p-1 rounded-xl shadow-sm">
              <Link to="/map" className={`px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${location.pathname === '/map' ? 'text-blue-600 bg-blue-50 font-semibold' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 font-medium'}`}>
                <MapPinIcon className="h-4 w-4" />
                {t('nav.map')}
              </Link>

              <Link to="/events" className={`px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${location.pathname === '/events' ? 'text-blue-600 bg-blue-50 font-semibold' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 font-medium'}`}>
                <CalendarDaysIcon className="h-4 w-4" />
                {t('nav.events')}
              </Link>

              {user && user.role === 'admin' && (
                <Link to="/admin/providers" className={`px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${location.pathname.includes('/admin') ? 'text-blue-600 bg-blue-50 font-semibold' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 font-medium'}`}>
                  <Cog6ToothIcon className="h-4 w-4" />
                  {t('nav.admin')}
                </Link>
              )}

              {user && user.role === 'provider' && (
                <Link to="/provider/dashboard" className={`px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${location.pathname.includes('/dashboard') ? 'text-blue-600 bg-blue-50 font-semibold' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 font-medium'}`}>
                  <ChartBarIcon className="h-4 w-4" />
                  {t('nav.dashboard')}
                </Link>
              )}

              {user && user.role === 'user' && (
                <Link to="/user/dashboard" className={`px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${location.pathname.includes('/dashboard') ? 'text-blue-600 bg-blue-50 font-semibold' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 font-medium'}`}>
                  <ChartBarIcon className="h-4 w-4" />
                  {t('nav.dashboard')}
                </Link>
              )}

              {user && (
                <Link to="/profile" className={`px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${location.pathname === '/profile' ? 'text-blue-600 bg-blue-50 font-semibold' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 font-medium'}`}>
                  <UserIcon className="h-4 w-4" />
                  {t('nav.profile')}
                </Link>
              )}
            </div>

            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* Auth Buttons */}
            {user ? (
              <button onClick={logout} className="text-sm font-medium text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg transition-colors">
                {t('nav.signout')}
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/login" className="text-sm font-medium text-white hover:bg-[#2372bd] px-3 py-2 transition-colors">
                  {t('nav.login')}
                </Link>
                <Link to="/register" className="text-sm font-bold text-white bg-[#1d5cab] hover:bg-[#2372bd] px-4 py-2 rounded-full shadow-sm transition-transform hover:scale-105">
                  {t('nav.register')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            {user ? (
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setIsMobileProfileOpen(!isMobileProfileOpen)}
                  className="p-2 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <UserIcon className="h-5 w-5" />
                </button>

                {/* Mobile Profile Dropdown */}
                {isMobileProfileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 max-h-[80dvh] overflow-y-auto bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-[3100]">
                    <Link
                      to="/profile"
                      className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => setIsMobileProfileOpen(false)}
                    >
                      <UserIcon className="h-4 w-4" />
                      {t('nav.profile')}
                    </Link>

                    <hr className="my-1 border-gray-200" />

                    <button
                      onClick={() => {
                        logout();
                        setIsMobileProfileOpen(false);
                      }}
                      className="flex items-center gap-3 w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4" />
                      {t('nav.signout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors">
                {t('nav.login')}
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* MOBILE BOTTOM NAVIGATION */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-[3000] bg-white border-t border-gray-200 shadow-lg safe-area-pb">
        <div className="flex items-center justify-around py-2 px-4">
          {/* Home */}
          <Link
            to="/"
            className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
              location.pathname === '/' ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <HomeIcon className="h-6 w-6" />
            <span className="text-xs mt-1">{t('nav.home', 'Home')}</span>
          </Link>

          {/* Map */}
          <Link
            to="/map"
            className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
              location.pathname === '/map' ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <MapPinIcon className="h-6 w-6" />
            <span className="text-xs mt-1">{t('nav.map')}</span>
          </Link>

          {/* Events */}
          <Link
            to="/events"
            className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
              location.pathname === '/events' ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <CalendarDaysIcon className="h-6 w-6" />
            <span className="text-xs mt-1">{t('nav.events')}</span>
          </Link>

          {/* Dynamic: Dashboard/Profile based on user role */}
          {user ? (
            user.role === 'admin' ? (
              <Link
                to="/admin/providers"
                className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
                  location.pathname.includes('/admin') ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Cog6ToothIcon className="h-6 w-6" />
                <span className="text-xs mt-1">{t('nav.admin')}</span>
              </Link>
            ) : (
              <Link
                to={user.role === 'provider' ? '/provider/dashboard' : '/user/dashboard'}
                className={`flex flex-col items-center p-2 rounded-lg transition-colors ${
                  location.pathname.includes('/dashboard') ? 'text-blue-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <ChartBarIcon className="h-6 w-6" />
                <span className="text-xs mt-1">{t('nav.dashboard')}</span>
              </Link>
            )
          ) : (
            <Link
              to="/login"
              className="flex flex-col items-center p-2 rounded-lg transition-colors text-gray-600 hover:text-gray-900"
            >
              <UserIcon className="h-6 w-6" />
              <span className="text-xs mt-1">{t('nav.login')}</span>
            </Link>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;
